import React, { ErrorInfo } from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import * as Sentry from "@sentry/react";

type State = { hasError: boolean; hasPermissionsError: boolean };
type Props = {
  fallback: React.ReactNode;
  permissionsFallback?: React.ReactNode;
  children: React.ReactNode;
};

export default class ErrorBoundary extends React.Component<Props, State> {
  static defaultProps = {
    fallback: null,
    permissionsFallback: null,
  };

  static contextType = NavigationContext;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, hasPermissionsError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if error is a navigation/redirect
    if (error.name === 'NavigationRedirect' || error.name === 'Redirect') {
      throw error; // Let React Router handle the redirect
    } else {
      const err: any = error;
      if (
        err &&
        err.response &&
        err.response.status &&
        err.response.status === 403
      ) {
        this.setState({ ...this.state, hasPermissionsError: true });
      } else {
        this.setState({ ...this.state, hasPermissionsError: false });
      }

      /**
       * Sentry error logging
       * URL to Sentry for this project:
       * DEV: https://sentry.io/organizations/avamae-software-solutions/issues/?project=1801833
       * PROD: https://sentry.io/organizations/avamae-software-solutions/issues/?project=1801843
       */
      // Old typescript version:
      // Sentry.withScope(scope => {
      //   scope.setExtras(errorInfo as any);
      //   Sentry.captureException(error);
      // });

      Sentry.withScope(scope => {
        Object.keys(errorInfo).forEach(key => {
          scope.setExtra(key, errorInfo[key as keyof typeof errorInfo]);
        });

        Sentry.captureException(error);
      });
    }
  }

  render() {
    if (this.state.hasPermissionsError && this.props.permissionsFallback)
      return this.props.permissionsFallback;

    if (this.state.hasError) return this.props.fallback;

    return this.props.children;
  }
}
